import React, { useState } from 'react';
import axios from 'axios';
import { getAuth } from "firebase/auth";

const ImageGenerator = ({ onImageGenerated }) => {
  const [prompt, setPrompt] = useState('');
  const [filter, setFilter] = useState('Default');
  const [style, setStyle] = useState('Cinematic');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const styleOptions = [
    'Cinematic',
    'Anime',
    'Anime XL',
    'Anime XL+',
    'Cinematic XL',
    'Fur',
    'Furtoon',
    'Hardcore XL',
    'Hyperanime',
    'Hyperreal',
    'Hyperreal XL+',
    'Illustration XL+',
    'K-Pop',
    'Photo XL+',
    'Render XL+'
  ].sort((a, b) => a.localeCompare(b));

  const filterOptions = [
    'Default',
    ...['Analog', 'Cinematic', 'Cyberpunk', 'Flash', 'Manga', 'Polaroid', 'Professional', 'Studio', 'VHS', 'Vintage'].sort()
  ];

  const handleGenerateImage = async () => {
    if (!prompt) return;
    setLoading(true);
    setImageUrl('');

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        console.error("❌ No user authenticated.");
        setLoading(false);
        return;
      }

      // Force token refresh
      await user.getIdToken(true);
      const token = await user.getIdToken();
      console.log("🔐 Sending token:", token);

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/generate-image`,{
        prompt,
        style,
        filter,
        image_size: "512x512",
        restore_faces: true
      }, {
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.data.imageUrl) {
        const url = response.data.imageUrl;
        if (url.startsWith('http')) {
          setImageUrl(url);
          onImageGenerated && onImageGenerated(response.data);
        } else {
          const base64Url = `data:image/png;base64,${url}`;
          setImageUrl(base64Url);
          onImageGenerated && onImageGenerated({ 
            ...response.data, 
            imageUrl: base64Url 
          });
        }
      }
    } catch (error) {
      console.error('❌ Error generating image:', error);
    }
    setLoading(false);
  };

  const formStyle = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    },
    title: {
      fontSize: '24px',
      marginBottom: '20px',
      color: '#333'
    },
    formLayout: {
      display: 'flex',
      gap: '30px',
      flexWrap: 'wrap'
    },
    formColumn: {
      flex: '1',
      minWidth: '300px'
    },
    formGroup: {
      marginBottom: '15px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      fontWeight: 'bold',
      color: '#555'
    },
    input: {
      width: '100%',
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px'
    },
    select: {
      width: '100%',
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '16px',
      backgroundColor: 'white'
    },
    button: {
      backgroundColor: '#4CAF50',
      color: 'white',
      padding: '10px 20px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      cursor: 'pointer',
      width: '100%',
      marginTop: '15px'
    },
    buttonDisabled: {
      backgroundColor: '#cccccc',
      cursor: 'not-allowed'
    },
    imageContainer: {
      marginTop: '20px',
      display: 'flex',
      justifyContent: 'center'
    },
    image: {
      maxWidth: '100%',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    promptContainer: {
      marginBottom: '20px'
    }
  };

  return (
    <div style={formStyle.container}>
      <h2 style={formStyle.title}>Generate Image</h2>

      <div style={formStyle.promptContainer}>
        <label style={formStyle.label}>Prompt</label>
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          style={formStyle.input}
          placeholder="Enter your prompt here..."
        />
      </div>

      <div style={formStyle.promptContainer}>
        <label style={formStyle.label}>Negative Prompt</label>
        <input
          type="text"
          value={negativePrompt}
          onChange={(e) => setNegativePrompt(e.target.value)}
          style={formStyle.input}
          placeholder="Enter what to avoid in the generation..."
        />
      </div>
      
      <div style={formStyle.formLayout}>
        <div style={formStyle.formColumn}>
          <div style={formStyle.formGroup}>
            <label style={formStyle.label}>Style</label>
            <select 
              value={style} 
              onChange={(e) => setStyle(e.target.value)}
              style={formStyle.select}
            >
              {styleOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div style={formStyle.formGroup}>
            <label style={formStyle.label}>Filter</label>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              style={formStyle.select}
            >
              {filterOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={formStyle.formColumn}>
          <button 
            onClick={handleGenerateImage} 
            disabled={loading}
            style={{
              ...formStyle.button,
              ...(loading ? formStyle.buttonDisabled : {})
            }}
          >
            {loading ? 'Generating...' : 'Generate Image'}
          </button>
        </div>
      </div>

      {imageUrl && (
        <div style={formStyle.imageContainer}>
          <img 
            src={imageUrl}
            alt="Generated"
            style={formStyle.image}
          />
        </div>
      )}
    </div>
  );
};

export default ImageGenerator;